import React, { useState, useEffect } from 'react';
import { getProductionLogs, fileBatch, deleteProductionLog, getPHTargets, updatePHTarget } from '../../../services/productionLogService';
import ProductionLogForm from './ProductionLogForm';
import ProductionLogList from './ProductionLogList';
import './ProductionLog.css';

const CATEGORIES = [
  { key: 'kombucha', label: 'Kombucha' },
  { key: 'waterKefir', label: 'Water Kefir' },
  { key: 'switchel', label: 'Switchel' },
  { key: 'fermentedVeg', label: 'Fermented Veg' },
  { key: 'brines', label: 'Brines' }
];

const FILING_REQUIREMENTS = {
  kombucha: { minPHReadings: 2, requiredFields: ['startDate', 'expirationDate', 'flavor', 'volumeGallons'] },
  waterKefir: { minPHReadings: 3, requiredFields: ['startDate', 'expirationDate', 'flavor', 'volumeGallons'] },
  switchel: { minPHReadings: 1, requiredFields: ['startDate', 'expirationDate', 'flavor', 'volumeGallons'] },
  fermentedVeg: { minPHReadings: 1, requiredFields: ['startDate', 'expirationDate', 'flavor', 'batchWeightLbs'] },
  brines: { minPHReadings: 1, requiredFields: ['startDate', 'expirationDate', 'brineVolumeGallons'] }
};

function canFileBatch(log) {
  const req = FILING_REQUIREMENTS[log.category];
  if (!req) return { ok: false, reason: 'Unknown category' };
  for (const field of req.requiredFields) {
    if (!log[field]) return { ok: false, reason: `Missing: ${field}` };
  }
  let phReadings = [];
  try { phReadings = JSON.parse(log.phReadings || '[]'); } catch {}
  const validReadings = phReadings.filter(r => r.actual);
  // Also count legacy pH fields as valid readings
  const legacyPH = (log.packagingPH || log.mixingPH || log.pH) ? 1 : 0;
  const totalReadings = validReadings.length + legacyPH;
  if (totalReadings < req.minPHReadings) {
    return { ok: false, reason: `Need ${req.minPHReadings} pH reading(s), have ${totalReadings}` };
  }
  return { ok: true };
}

export default function ProductionLog() {
  const [view, setView] = useState('main');
  const [selectedCategory, setSelectedCategory] = useState('kombucha');
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('kombucha');
  const [phTargets, setPhTargets] = useState([]);
  const [editingTargets, setEditingTargets] = useState(false);
  const [targetEdits, setTargetEdits] = useState({});

  useEffect(() => {
    if (view === 'main') {
      loadAllLogs();
      loadPHTargets();
    }
  }, [view]);

  async function loadAllLogs() {
    setLoading(true);
    const results = await Promise.all(
      CATEGORIES.map(c => getProductionLogs(c.key).then(r => ({
        category: c.key,
        categoryLabel: c.label,
        records: r.success ? r.data : []
      })))
    );
    const all = results.flatMap(r =>
      r.records.map(rec => ({ ...rec, category: r.category, categoryLabel: r.categoryLabel }))
    );
    all.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    setAllLogs(all);
    setLoading(false);
  }

  async function loadPHTargets() {
    const res = await getPHTargets();
    if (res.success) setPhTargets(res.data);
  }

  function getPHTarget(category, readingName) {
    const t = phTargets.find(t => t.category === category && t.readingName === readingName);
    return t ? t.targetPH : '';
  }

  async function handleFile(log) {
    const check = canFileBatch(log);
    if (!check.ok) {
      alert(`Cannot file batch: ${check.reason}`);
      return;
    }
    if (!window.confirm(`File batch ${log.batchId}? It will be moved to the log archive.`)) return;
    const res = await fileBatch(log.category, log.batchId);
    if (res.success) loadAllLogs();
    else alert('Error filing batch: ' + res.error);
  }

  async function handleDelete(log) {
    if (!window.confirm(`Delete batch ${log.batchId}?`)) return;
    const res = await deleteProductionLog(log.category, log.batchId);
    if (res.success) loadAllLogs();
    else alert('Error deleting: ' + res.error);
  }

  function handleEdit(log) {
    setSelectedCategory(log.category);
    setEditRecord(log);
    setView('form');
  }

  function handleNew() {
    setEditRecord(null);
    setView('form');
  }

  async function handleSaveTargets() {
    await Promise.all(
      Object.entries(targetEdits).map(([key, value]) => {
        const [category, readingName] = key.split('__');
        return updatePHTarget(category, readingName, value);
      })
    );
    setEditingTargets(false);
    setTargetEdits({});
    loadPHTargets();
  }

  const activeLogs = allLogs.filter(l => l.filed !== 'yes');
  const filedLogs = allLogs.filter(l => l.filed === 'yes');
  const filedByCategory = (cat) => {
    if (cat === 'fermentedVeg') return filedLogs.filter(l => l.category === 'fermentedVeg' || l.category === 'brines');
    return filedLogs.filter(l => l.category === cat);
  };

  if (view === 'form') {
    return (
      <ProductionLogForm
        category={selectedCategory}
        categories={CATEGORIES}
        editRecord={editRecord}
        onSaved={() => setView('main')}
        onCancel={() => setView('main')}
        allLogs={allLogs}
        phTargets={phTargets}
        getPHTarget={getPHTarget}
      />
    );
  }

  return (
    <div className="pl-container">
      <div className="pl-header">
        <h1>CC Production Log</h1>
        <div className="pl-header-actions">
          <button className="pl-btn-secondary" onClick={() => setEditingTargets(true)}>Edit Target PHs</button>
          <button className="pl-btn-primary" onClick={handleNew}>+ New Batch</button>
        </div>
      </div>

      {/* pH Target Editor */}
      {editingTargets && (
        <div className="pl-target-editor">
          <h3>Edit Target pH Values</h3>
          {phTargets.map((t, i) => (
            <div key={i} className="pl-form-row">
              <div className="pl-form-group" style={{flex: 2}}>
                <label>{t.category} — {t.readingName}</label>
                <small>{t.description}</small>
              </div>
              <div className="pl-form-group">
                <label>Target pH</label>
                <input
                  type="number"
                  step="0.1"
                  value={targetEdits[`${t.category}__${t.readingName}`] !== undefined
                    ? targetEdits[`${t.category}__${t.readingName}`]
                    : t.targetPH}
                  onChange={e => setTargetEdits(prev => ({
                    ...prev,
                    [`${t.category}__${t.readingName}`]: e.target.value
                  }))}
                />
              </div>
            </div>
          ))}
          <div className="pl-form-actions">
            <button className="pl-btn-secondary" onClick={() => { setEditingTargets(false); setTargetEdits({}); }}>Cancel</button>
            <button className="pl-btn-primary" onClick={handleSaveTargets}>Save Targets</button>
          </div>
        </div>
      )}

      {/* Active Batches */}
      <div className="pl-section">
        <h2 className="pl-section-title">🟢 In Production</h2>
        {loading ? (
          <div className="pl-loading">Loading...</div>
        ) : activeLogs.length === 0 ? (
          <div className="pl-empty">No batches currently in production.</div>
        ) : (
          <ProductionLogList
            logs={activeLogs}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onFile={handleFile}
            canFile={canFileBatch}
            showFileButton={true}
          />
        )}
      </div>

      {/* Filed Batches */}
      <div className="pl-section">
        <h2 className="pl-section-title">📁 Batch Log</h2>
        <div className="pl-category-tabs">
          {[
            { key: 'kombucha', label: 'Kombucha' },
            { key: 'waterKefir', label: 'Water Kefir' },
            { key: 'switchel', label: 'Switchel' },
            { key: 'fermentedVeg', label: 'Fermented Veg & Brines' }
          ].map(c => (
            <button
              key={c.key}
              className={`pl-tab ${activeTab === c.key ? 'active' : ''}`}
              onClick={() => setActiveTab(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        {filedByCategory(activeTab).length === 0 ? (
          <div className="pl-empty">No filed batches in this category.</div>
        ) : (
          <ProductionLogList
            logs={filedByCategory(activeTab)}
            onEdit={handleEdit}
            onDelete={handleDelete}
            showFileButton={false}
          />
        )}
      </div>
    </div>
  );
}
